import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, ExclamationTriangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, formatMeterRaw } from './dailyDataUtils'
import { SlotAnalysisSummary } from './SlotAnalysisPanel'

const TOTAL_ROWS = [
  { key: 'turnover', label: 'Turnover' },
  { key: 'cancelcredit', label: 'Cancel credits' },
  { key: 'cashin', label: 'Cash in' },
  { key: 'cashdrop', label: 'Cash drop' },
  { key: 'cashlessin', label: 'Cashless in' },
  { key: 'cashlessout', label: 'Cashless out' },
  { key: 'creditwin', label: 'Credit win' },
]

export default function MeterReconciliationPanel({
  tradingDay,
  onTradingDayChange,
  refreshKey,
  title = 'Reconciliation',
  showDatePicker = true,
  prefetchedReport = null,
  prefetchedLoading = false,
  skipOwnFetch = false,
  compact = false,
  hideSlotAnalysis = false,
}) {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showImportRuns, setShowImportRuns] = useState(false)
  const [expanded, setExpanded] = useState(!compact)

  const usePrefetch =
    skipOwnFetch ||
    (prefetchedReport != null && prefetchedReport.trading_day === tradingDay)

  const load = useCallback(async () => {
    if (!tradingDay || skipOwnFetch) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ trading_day: tradingDay })
      const res = await authFetch(getLocalApiUrl(`/api/daily-data/meter/reconciliation?${params}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load reconciliation')
      setReport(json.data)
    } catch (e) {
      setError(e.message)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [tradingDay, skipOwnFetch])

  useEffect(() => {
    if (skipOwnFetch) {
      setReport(prefetchedReport)
      setLoading(false)
      setError(null)
      return
    }
    if (prefetchedReport != null && prefetchedReport.trading_day === tradingDay) {
      setReport(prefetchedReport)
      setLoading(false)
      setError(null)
      return
    }
    load()
  }, [load, refreshKey, skipOwnFetch, prefetchedReport, tradingDay])

  const displayReport = skipOwnFetch
    ? prefetchedReport
    : (prefetchedReport?.trading_day === tradingDay ? prefetchedReport : report)
  const displayLoading = skipOwnFetch ? prefetchedLoading : loading
  const recon = displayReport?.reconciliation
  const completeness = recon?.completeness
  const totals = recon?.totals
  const resetCount = recon?.resets?.egm_count || 0

  return (
    <div className={`bg-white shadow rounded-lg border-l-4 border-indigo-500 ${compact ? 'p-3' : 'p-6'}`}>
      {compact ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full text-left"
          aria-expanded={expanded}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 min-w-0">
              <ChevronDownIcon className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              <span className="text-sm font-semibold text-gray-900">{title}</span>
              {resetCount > 0 && (
                <span className="text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  {resetCount} reset{resetCount === 1 ? '' : 's'}
                </span>
              )}
            </span>
            <span className="text-[11px] text-gray-500">{expanded ? 'Hide' : 'Show'}</span>
          </span>
          {!expanded && completeness?.distinct_egms > 0 && (
            <span className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
              <span className="rounded bg-gray-50 px-2 py-1">
                <span className="block text-gray-500 uppercase">EGMs</span>
                <span className="font-semibold tabular-nums text-gray-900">{completeness.distinct_egms}</span>
              </span>
              <span className="rounded bg-gray-50 px-2 py-1">
                <span className="block text-gray-500 uppercase">Cash in</span>
                <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(totals?.cashin)}</span>
              </span>
              <span className="rounded bg-gray-50 px-2 py-1">
                <span className="block text-gray-500 uppercase">Ticket out</span>
                <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(totals?.cashlessout)}</span>
              </span>
              <span className="rounded bg-gray-50 px-2 py-1">
                <span className="block text-gray-500 uppercase">Cancel cr.</span>
                <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(totals?.cancelcredit)}</span>
              </span>
            </span>
          )}
        </button>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Trading-day roll-up from ingested meter readings (completeness, deltas, resets).
            </p>
            {displayReport?.label && (
              <p className={`text-xs mt-1 ${
                recon?.completeness?.partial_day ? 'text-amber-800' : 'text-gray-500'
              }`}
              >
                {displayReport.label}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={displayLoading || !tradingDay || skipOwnFetch}
            className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
            title={skipOwnFetch ? 'Use the page Refresh button to reload' : undefined}
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${displayLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      )}

      {compact && !expanded ? null : (
        <div className={compact ? 'mt-3' : ''}>
          {compact && displayReport?.label && (
            <p className={`text-xs mb-3 ${
              recon?.completeness?.partial_day ? 'text-amber-800' : 'text-gray-500'
            }`}
            >
              {displayReport.label}
            </p>
          )}

      {showDatePicker && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Trading day</label>
          <input
            type="date"
            value={tradingDay || ''}
            onChange={(e) => onTradingDayChange?.(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
        </div>
      )}

      {error && !skipOwnFetch && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {displayLoading && !displayReport && (
        <p className="text-sm text-gray-500">Loading reconciliation…</p>
      )}

      {!displayLoading && displayReport && !recon?.completeness?.distinct_egms && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          No meter readings in the database for {formatTradingDay(tradingDay)}.
          {' '}
          <Link to="/monitor-safe/daily-data/meter" className="text-primary-600 hover:underline font-medium">
            Import meter data
          </Link>
          {' '}or choose another trading day above.
        </div>
      )}

      {recon && recon.completeness?.distinct_egms > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">EGMs</div>
              <div className="text-lg font-semibold">{completeness?.distinct_egms ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Snapshots</div>
              <div className="text-lg font-semibold">{completeness?.total_snapshots ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Avg / EGM</div>
              <div className="text-lg font-semibold">{completeness?.avg_snapshots_per_egm ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Imports today</div>
              <div className="text-lg font-semibold">{displayReport?.import_run_count ?? 0}</div>
            </div>
          </div>

          {(completeness?.snapshot_min || completeness?.snapshot_max) && (
            <p className="text-xs text-gray-500 mb-4">
              Meter snapshot span: {formatDateTime(completeness.snapshot_min)} — {formatDateTime(completeness.snapshot_max)}
              {displayReport?.last_import_at && (
                <> · Last import {formatDateTime(displayReport.last_import_at)}</>
              )}
            </p>
          )}

          {(recon.resets?.egm_count > 0) && (
            <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <div>
                <strong>{recon.resets.egm_count} EGM(s)</strong> with meter reset detected ({recon.resets.event_count} event(s)).
                Cross-check IGT Machine Meter Variance; Banking Summary may align after venue Accept.
              </div>
            </div>
          )}

          <h3 className="text-sm font-semibold text-gray-900 mb-2">Daily deltas (AUD)</h3>
          <table className="min-w-full divide-y divide-gray-200 mb-6">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">PaySafeGO total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {TOTAL_ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-sm text-gray-800">{label}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono">{formatMeterRaw(recon.totals?.[key])}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {displayReport?.import_runs?.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <button
                type="button"
                onClick={() => setShowImportRuns(!showImportRuns)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${showImportRuns ? 'rotate-180' : ''}`}
                />
                Import runs ({displayReport.import_runs.length} file{displayReport.import_runs.length !== 1 ? 's' : ''})
              </button>
              {showImportRuns && (
                <ul className="mt-2 text-sm text-gray-700 space-y-1 ml-6">
                  {displayReport.import_runs.map((run) => (
                    <li key={run.id} className="font-mono text-xs">
                      {formatDateTime(run.run_at)} — {run.status} — imported {run.imported}
                      {run.file_path ? ` — ${run.file_path}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            Totals are summed from ingested meter counter deltas (stored in cents, shown as AUD). Compare with venue Banking Summary in your club&apos;s separate reporting process.
          </p>

          {recon?.slot_analysis !== undefined && recon?.slot_analysis !== null && !hideSlotAnalysis && (
            <>
              <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">
                Slot Analysis Summary (from FactSlotAnalysisDaily)
              </h3>
              <SlotAnalysisSummary slot={recon.slot_analysis} />
            </>
          )}

          {recon?.sessions !== undefined && recon?.sessions !== null && (
            <>
              <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">
                Gaming Sessions Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3">
                  <div className="text-xs text-indigo-600 uppercase font-medium">Total Sessions</div>
                  <div className="text-xl font-bold text-indigo-900">
                    {recon.sessions.total_sessions.toLocaleString()}
                  </div>
                  <div className="text-xs text-indigo-600 mt-1">
                    {recon.sessions.carded_percentage}% carded
                  </div>
                </div>
                <div className="rounded-lg bg-green-50 border border-green-100 p-3">
                  <div className="text-xs text-green-600 uppercase font-medium">Unique Players</div>
                  <div className="text-xl font-bold text-green-900">
                    {recon.sessions.unique_players.toLocaleString()}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {recon.sessions.unique_egms} EGMs
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <div className="text-xs text-blue-600 uppercase font-medium">Turnover</div>
                  <div className="text-xl font-bold text-blue-900">
                    {formatMeterRaw(recon.sessions.total_turnover)}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">From sessions</div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                  <div className="text-xs text-amber-600 uppercase font-medium">Games Played</div>
                  <div className="text-xl font-bold text-amber-900">
                    {recon.sessions.total_games.toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">Total spins</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Cash In</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_cashin)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Cash Out</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_cashout)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Credit Win</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_creditwin)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Carded vs Uncarded</div>
                  <div className="text-lg font-semibold">
                    {recon.sessions.carded_sessions} / {recon.sessions.uncarded_sessions}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Gaming sessions data from FactPlayerSession file (individual play sessions). 
                Values shown in AUD (converted from cents).
              </p>
            </>
          )}
        </>
      )}
        </div>
      )}
    </div>
  )
}
